import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { Constants } from '../../models/constants.model';
import { AuthorizationService } from '../authorization.service';
import { MessageService } from '../message.service';
import { MessageType } from '../../models/message';
import { CacheService } from '../cache.service';
import { ArrayHelper } from '../../utils/array-helper';
import { StringHelper } from '../../utils/string-helper';
import { HttpHelper } from '../../utils/http-helper';
import { UrlHelper } from '../../utils/url-helper';
import { StatusId } from '../../models/status-id';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../authorization.service";
import * as i3 from "../message.service";
import * as i4 from "../cache.service";
var BaseService = /** @class */ (function () {
    function BaseService(http, authorization, messages, cache) {
        this.http = http;
        this.authorization = authorization;
        this.messages = messages;
        this.cache = cache;
        /**
         * Request-ContentType
         *
         * @protected
         * @memberof BaseService
         */
        this.contentType = Constants.ContentType.JSON;
        /**
         * Indicates when a login-redirect is pending
         *
         * @protected
         * @memberof BaseService
         */
        this.isRedirectPending = false;
        this.apiBaseUrl = UrlHelper.trimUrl(AppConfigService.settings.apiUrl);
        this.numRetry = AppConfigService.settings.debug ? 0 : 2;
        this.loadHttpOptions();
    }
    Object.defineProperty(BaseService.prototype, "authorizationType", {
        /**
         * Service required authentication
         *
         * @protected
         * @memberof BaseService
         */
        get: function () {
            return AuthorizationType.Login;
        },
        enumerable: true,
        configurable: true
    });
    BaseService.prototype.getAllRequest = function (url, params) {
        if (url === void 0) { url = null; }
        if (params === void 0) { params = null; }
        if (this.shouldReturnEmpty) {
            return this.emptyResponse;
        }
        var endpoint = url == null ? this.endpointUri : url;
        return this.http.get(this.getFullUrl(endpoint, null, params), this.httpOptions)
            .pipe(retry(this.numRetry), catchError(this.handleError("GET: " + endpoint)));
    };
    BaseService.prototype.getRequest = function (id, url, params) {
        if (url === void 0) { url = null; }
        if (params === void 0) { params = null; }
        if (this.shouldReturnEmpty) {
            return this.emptyResponse;
        }
        var endpoint = url == null ? this.endpointUri : url;
        return this.http.get(this.getFullUrl(endpoint, id, params), this.httpOptions)
            .pipe(retry(this.numRetry), catchError(this.handleError("GET: " + endpoint)));
    };
    BaseService.prototype.getRequestCustom = function (url) {
        if (url === void 0) { url = null; }
        if (this.shouldReturnEmpty) {
            return this.emptyResponse;
        }
        return this.http.get(url, this.httpOptions)
            .pipe(retry(this.numRetry), catchError(this.handleError("GET-Custom: " + url)));
    };
    BaseService.prototype.addRequest = function (url, entity, headers) {
        if (url === void 0) { url = null; }
        if (this.shouldReturnEmpty) {
            return this.emptyResponse;
        }
        var endpoint = url == null ? this.endpointUri : url;
        var options = headers ? this.mergeHeaders(this.httpOptions, headers) : this.httpOptions;
        options.observe = 'response';
        return this.http.post(this.getFullUrl(endpoint, null), entity, options)
            .pipe(retry(this.numRetry), catchError(this.handleError("POST: " + endpoint)));
    };
    BaseService.prototype.updateRequest = function (id, url, entity, headers) {
        if (url === void 0) { url = null; }
        if (this.shouldReturnEmpty) {
            return this.emptyResponse;
        }
        var endpoint = url == null ? this.endpointUri : url;
        var options = headers ? this.mergeHeaders(this.httpOptions, headers) : this.httpOptions;
        options.observe = 'response';
        return this.http.put(this.getFullUrl(endpoint, id), entity, this.httpOptions)
            .pipe(retry(this.numRetry), catchError(this.handleError("PUT: " + endpoint)));
    };
    BaseService.prototype.patchRequest = function (id, url, entity, headers) {
        if (url === void 0) { url = null; }
        if (this.shouldReturnEmpty) {
            return this.emptyResponse;
        }
        var endpoint = url == null ? this.endpointUri : url;
        var options = headers ? this.mergeHeaders(this.httpOptions, headers) : this.httpOptions;
        options.observe = 'response';
        return this.http.patch(this.getFullUrl(endpoint, id), entity, this.httpOptions)
            .pipe(retry(this.numRetry), catchError(this.handleError("PATCH: " + endpoint)));
    };
    BaseService.prototype.deleteRequest = function (id, url) {
        if (url === void 0) { url = null; }
        if (this.shouldReturnEmpty) {
            return this.emptyResponse;
        }
        var endpoint = url == null ? this.endpointUri : url;
        return this.http.delete(this.getFullUrl(endpoint, id), this.httpOptions)
            .pipe(catchError(this.handleError("DELETE: " + endpoint)));
    };
    Object.defineProperty(BaseService.prototype, "shouldReturnEmpty", {
        /**
         * Returns true if any operation is pending, which should prevent http-respones-handling
         *
         * @readonly
         * @private
         * @type {boolean}
         * @memberof BaseService
         */
        get: function () {
            this.ensureLogin();
            return this.isRedirectPending;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseService.prototype, "emptyResponse", {
        /**
         * Returns an empty observeable
         *
         * @readonly
         * @private
         * @type {Observable<any>}
         * @memberof BaseService
         */
        get: function () {
            return new Observable();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Handle request-errors
     *
     * @private
     * @template T
     * @param {string} [operation='unknown']
     * @param {T} [result]
     * @returns
     * @memberof BaseService
     */
    BaseService.prototype.handleError = function (operation) {
        var _this = this;
        if (operation === void 0) { operation = 'unknown'; }
        return function (error) {
            if (error.error instanceof ErrorEvent) {
                // A client-side or network error occurred.
                _this.messages.add(operation + ': ' + error.error.message, MessageType.Error);
            }
            else {
                // The backend returned an unsuccessful response code.
                var err = 'Error on http-request for ' + error.url + ': ' + error.status + ' ' + HttpHelper.getStatusText(error.status);
                _this.messages.add(operation + " => " + err, MessageType.Debug);
            }
            return throwError(error);
        };
    };
    /**
     * Set endpoint headers
     *
     * @private
     * @returns {(void | never)}
     * @memberof BaseService
     */
    BaseService.prototype.loadHttpOptions = function () {
        var headers = {};
        headers[Constants.Header.Request.ContentType] = this.contentType;
        if (this.authorizationType !== AuthorizationType.None) {
            this.ensureLogin();
            // set header
            headers[Constants.Header.Request.Authorization] = this.authorization.getAuthorizationHeaderValue();
        }
        this.httpOptions = {
            headers: new HttpHeaders(headers)
        };
    };
    /**
     * Starts login if required
     *
     * @private
     * @return {*}  {(void | never)}
     * @memberof BaseService
     */
    BaseService.prototype.ensureLogin = function () {
        if (this.authorizationType === AuthorizationType.None || this.authorization.isAuthorized() || this.isRedirectPending) {
            return;
        }
        // redirect log oauth
        this.isRedirectPending = true;
        switch (this.authorizationType) {
            case AuthorizationType.Anonymous:
                this.authorization.logInAnonymous();
                break;
            case AuthorizationType.Login:
                this.authorization.logIn();
                break;
            default:
                this.messages.add('authorization for type "' + this.authorization + '" not implemented', MessageType.Debug);
        }
    };
    /**
     * Returns the full qualified url for an endpoint
     *
     * @private
     * @param {string} url
     * @param {IKeyValuePair[]} [params=null]
     * @returns {string}
     * @memberof BaseService
     */
    BaseService.prototype.getFullUrl = function (url, id, params) {
        if (params === void 0) { params = null; }
        var result = this.apiBaseUrl + '/' + url + '/';
        // let result = this.apiBaseUrl + '/' + url + (url.includes('/') ? '' : '/');
        if (StringHelper.hasAny(id)) {
            result += id;
        }
        if (ArrayHelper.hasAny(params)) {
            result += '?' + params
                .filter(function (kv) { return !StringHelper.isNullOrEmpty(kv.value); })
                .map(function (kv) { return kv.key + "=" + encodeURIComponent(kv.value); })
                .join('&');
        }
        return result;
    };
    BaseService.prototype.mergeHeaders = function (httpOptions, additionalHeaders) {
        var keyValue = {};
        // existing headers
        httpOptions.headers.forEach(function (key, value) { keyValue[key] = value[0]; });
        // additional header
        additionalHeaders.forEach(function (kv) { keyValue[kv.key] = kv.value; });
        // set headers
        httpOptions.headers = new HttpHeaders(keyValue);
        return httpOptions;
    };
    // TODO: reduce to minimal func calls, model return data
    BaseService.prototype.getReservabilityToken = function (room, searchDate) {
        var pipe = [
            // room unavailable: 0 - 3
            function (r) { return r.StatusId === StatusId.rrGesperrtBis && r.StatusDate >= searchDate; },
            function (r) { return r.StatusId === StatusId.rrReservierbarBis && r.StatusDate < searchDate; },
            function (r) { return r.StatusAllowOccupation !== true; },
            function (r) { return r.IsWorkingDay !== true; },
            // rights available: 4 - 6
            function (r) { return r.ReservationRightId === Constants.ReservationsRecht.Reservable; },
            function (r) { return r.ReservationRightId === Constants.ReservationsRecht.ProvReservable; },
            function (r) { return r.ReservationRightId === Constants.ReservationsRecht.Requestable; },
            // rights unavailable: 7
            function (r) { return r.ReservationRightId === Constants.ReservationsRecht.Visible; },
        ];
        var appliedRule = pipe.findIndex(function (p) { return p(room); });
        if (appliedRule === -1 || appliedRule > 7) {
            throw new Error('Mismatch: ' + room);
        }
        if (appliedRule < 4) {
            return { isRessourceAvailable: false, isBookingAllowed: false, actionLabel: null };
        }
        if (appliedRule < 7) {
            return { isRessourceAvailable: true, isBookingAllowed: true, actionLabel: appliedRule !== 6 ? 'RESERVE' : 'REQUEST' };
        }
        if (appliedRule === 7) {
            return { isRessourceAvailable: true, isBookingAllowed: false, actionLabel: null };
        }
    };
    BaseService.ngInjectableDef = i0.defineInjectable({ factory: function BaseService_Factory() { return new BaseService(i0.inject(i1.HttpClient), i0.inject(i2.AuthorizationService), i0.inject(i3.MessageService), i0.inject(i4.CacheService)); }, token: BaseService, providedIn: "root" });
    return BaseService;
}());
export { BaseService };
export var AuthorizationType;
(function (AuthorizationType) {
    // No token required
    AuthorizationType[AuthorizationType["None"] = 1] = "None";
    // Anonymous token in scope Public
    AuthorizationType[AuthorizationType["Anonymous"] = 2] = "Anonymous";
    // Authenticate
    AuthorizationType[AuthorizationType["Login"] = 3] = "Login";
})(AuthorizationType || (AuthorizationType = {}));
